import { Stack } from '@mui/material'
import {
  MRT_RowData,
  MRT_TableInstance,
  MRT_TableOptions,
  useMaterialReactTable,
} from 'material-react-table'
import { ReactNode } from 'react'
import { TomTableActionsProps } from '../theme/components/table/molecules/TomTableActions'
import { TomTableNoResultComponent } from '../theme/components/table/molecules/TomTableNoResultComponent'
import { IDataExportConfig } from '../theme/components/table/utils/xlsxUtils'
import { useDefaultMRTOptions } from './use-default-mrt-options-hook'
import {
  ColumnPreferencesConfig,
  useTomTableColumnPreferences,
} from './use-tom-table-column-preferences'

export type TomTableDataExportProps<TData extends MRT_RowData> = {
  title: string
  enableDataExport?: boolean
  dataExportConfig?: IDataExportConfig<TData>
  customExportCsv?: (table: MRT_TableInstance<TData>) => void
  customExportXlsx?: (table: MRT_TableInstance<TData>) => void
}

type TomTableMeta<TData extends MRT_RowData> = {
  hasExternalTopToolbarCustomActions?: boolean
  renderTopToolbarLeftCustomActions?: (table: unknown) => ReactNode
}

export const useTomTable = <TData extends MRT_RowData>(
  tableOptions: MRT_TableOptions<TData>,
  tableActions?: TomTableActionsProps<TData>[],
  dataExport?: TomTableDataExportProps<TData>,
  columnPreferences?: ColumnPreferencesConfig<TData>,
) => {
  const defaultMRTOptions = useDefaultMRTOptions<TData>(tableActions, dataExport)
  const columnPrefs = useTomTableColumnPreferences(columnPreferences)

  return useMaterialReactTable({
    ...defaultMRTOptions,
    ...tableOptions,
    ...(columnPrefs && {
      state: {
        ...tableOptions.state,
        columnVisibility: columnPrefs.columnVisibility,
        columnOrder: columnPrefs.columnOrder,
      },
      onColumnVisibilityChange: columnPrefs.onColumnVisibilityChange,
      onColumnOrderChange: columnPrefs.onColumnOrderChange,
    }),

    //this is the workaround for https://omoqo.atlassian.net/browse/CGL-1273
    //UX want the alert banner to be below the custom actions, but material-react-table only support positionToolbarAlertBanner: 'head' | 'top' | 'bottom' | 'none'
    positionToolbarAlertBanner: 'none',
    renderToolbarAlertBannerContent: () => <></>,
    meta: {
      ...(defaultMRTOptions.meta as TomTableMeta<TData> | undefined),
      ...(tableOptions.meta as TomTableMeta<TData> | undefined),
      hasExternalTopToolbarCustomActions: !!tableOptions.renderTopToolbarCustomActions,
    },
    renderTopToolbarCustomActions: props => {
      const selectedRows = props.table.getSelectedRowModel().rows
      const meta = (props.table.options.meta as TomTableMeta<TData> | undefined) ?? {}
      const leftActions = meta.renderTopToolbarLeftCustomActions?.(props.table)
      const rightActions = tableOptions.renderTopToolbarCustomActions?.(props)

      return (
        <Stack direction='column' width='100%'>
          {(leftActions || rightActions) && (
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              sx={{ padding: 1 }}
              width='100%'
            >
              <Stack direction='row' alignItems='center'>
                {leftActions}
              </Stack>
              <Stack direction='row' alignItems='center' sx={{ ml: 'auto' }}>
                {rightActions}
              </Stack>
            </Stack>
          )}

          {selectedRows.length > 0 &&
            (
              tableOptions.renderToolbarAlertBannerContent ??
              defaultMRTOptions.renderToolbarAlertBannerContent
            )?.({
              groupedAlert: undefined,
              selectedAlert: undefined,
              table: props.table,
            })}
        </Stack>
      )
    },
    muiTopToolbarProps: {
      sx: {
        '& > .MuiBox-root': {
          padding: '0 !important',
        },
      },
    },
    renderEmptyRowsFallback:
      tableOptions.renderEmptyRowsFallback ?? (() => <TomTableNoResultComponent />),
  })
}
